import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialLanguageField } from '@app/modules/social-export/components/inputs/SocialLanguageField';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import {
  getBaseExportSchema,
  MessageFormatter,
} from '@app/modules/social-export/components/validation/base-export-schema';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { YoutubePlaylistsField } from '@app/modules/social-export/platforms/youtube/playlist-field/YoutubePlaylistsField';
import { YoutubeCategoryField } from '@app/modules/social-export/platforms/youtube/YoutubeCategoryField';
import { YoutubeMadeForKidsField } from '@app/modules/social-export/platforms/youtube/YoutubeMadeForKidsField';
import { YoutubePrivacyStatusField } from '@app/modules/social-export/platforms/youtube/YoutubePrivacyStatusField';
import { FormSection } from '@app/modules/social-export/screens/manage/platforms/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid } from '@mui/material';
import React from 'react';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

export interface YoutubePublishValues {
  title: string;
  content: string;
  tags: string;
  thumbnailUrl: string | undefined;
  thumbType: 'DEFAULT' | 'IMAGE_URL' | 'VIDEO_FRAME';
  language: string | null;
  privacyStatus: string;
  madeForKids: boolean;
  publishAt: string | null;
  categoryId: string;
  playlistsIds: socialExport.YoutubePlaylistDto[] | null;
}

type YoutubePublishFormProps = BaseExportFormProps<
  socialExport.YoutubePublishDto,
  socialExport.YoutubeSettingsDto
>;

const requiredMsg = { id: 'common.required', defaultMessage: 'Requerido' };
const maxPlaylistsMsg = {
  id: 'socialExport.validation.maxPlaylists',
  defaultMessage: 'Maximum 10 playlists allowed',
};

function getYoutubePublishValidationSchema(t: MessageFormatter) {
  const youtubeSettingsSchema = z.object({
    language: z
      .string()
      .nullable()
      .optional()
      .transform((val) => (val === '' ? null : val)),
    privacyStatus: z.string().min(1, t(requiredMsg)),
    madeForKids: z.boolean(),
    categoryId: z
      .string()
      .nullable()
      .optional()
      .transform((val) => (val === '' ? null : val)),
  });

  return getBaseExportSchema(t)
    .merge(youtubeSettingsSchema)
    .extend({
      title: z.string().trim().min(1, t(requiredMsg)),
      content: z.string().trim().min(1, t(requiredMsg)),
      tags: z.string(),
      thumbnailUrl: z.string().optional(),
      thumbType: z.enum(['DEFAULT', 'IMAGE_URL', 'VIDEO_FRAME']),
      thumbOffsetMs: z.number().nullable().optional(),
      playlistsIds: z.array(z.any()).max(10, t(maxPlaylistsMsg)).nullable().optional(),
    })
    .refine(
      (data) => {
        if (data.thumbType === 'IMAGE_URL') {
          return !!data.thumbnailUrl;
        }
        return true;
      },
      { message: t(requiredMsg), path: ['thumbnailUrl'] },
    )
    .refine(
      (data) => {
        if (data.thumbType === 'VIDEO_FRAME') {
          return data.thumbOffsetMs !== null && data.thumbOffsetMs !== undefined;
        }
        return true;
      },
      { message: t(requiredMsg), path: ['thumbOffsetMs'] },
    );
}

export function YoutubePublishForm({
  fragment,
  onUpload,
  defaultSettings,
  videoUrl,
  actions,
}: YoutubePublishFormProps) {
  const { $t } = useIntl();
  const schema = React.useMemo(() => getYoutubePublishValidationSchema((msg) => $t(msg)), [$t]);
  const initialValues: YoutubePublishValues = {
    title: fragment.title,
    content: fragment.description ?? '',
    tags: fragment.tags?.join(', '),
    thumbnailUrl: undefined,
    thumbType: 'DEFAULT',
    language: defaultSettings?.language ?? null,
    privacyStatus: defaultSettings?.privacyStatus ?? 'private',
    madeForKids: defaultSettings?.madeForKids ?? false,
    publishAt: null,
    categoryId: defaultSettings?.categoryId ?? '',
    playlistsIds: [],
  };

  const beforeSubmit = (data: YoutubePublishValues): socialExport.YoutubePublishDto => {
    return {
      title: data.title,
      description: data.content,
      tags: data.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      thumbnailUrl:
        data.thumbType === 'IMAGE_URL' || data.thumbType === 'VIDEO_FRAME'
          ? data.thumbnailUrl
          : undefined,
      language: data.language,
      privacyStatus: data.privacyStatus as socialExport.YoutubePublishDto['privacyStatus'],
      madeForKids: data.madeForKids,
      publishAt: data.publishAt || undefined,
      categoryId: data.categoryId || undefined,
      playlistsIds: (data.playlistsIds ?? []).map((playlist) => playlist.id),
    };
  };

  return (
    <FormContainer
      values={initialValues}
      onSuccess={async (formData) => {
        const processedData = beforeSubmit(formData);
        await onUpload(processedData);
      }}
      resolver={zodResolver(schema)}
    >
      <FormSection
        title={<FormattedMessage id="socialExport.form.content" defaultMessage="Contenido" />}
      >
        <Grid container>
          <Grid item md={12}>
            <TitleAndContentFieldsMui />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <TextFieldElement
              name="tags"
              label={<FormattedMessage id="socialExport.form.tags" defaultMessage="Tags" />}
              helperText={'Valores separados por coma'}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={10}>
            <YoutubePlaylistsField stationId={fragment.stationId} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={10}>
            <ThumbFieldsMui showFrameImageToField={true} videoUrl={videoUrl} />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={
          <FormattedMessage
            id="socialExport.youtube.publishParams"
            defaultMessage="Parámetros de publicación"
          />
        }
      >
        <Grid container>
          <Grid item md={8}>
            <YoutubeCategoryField stationId={fragment.stationId} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <SocialLanguageField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <YoutubePrivacyStatusField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <YoutubeMadeForKidsField />
          </Grid>
        </Grid>
        <Grid container mt={-4}>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      {actions}
    </FormContainer>
  );
}
